/**
 * Shared Tailwind class strings for the dashboard.
 *
 * The dashboard used to style itself with its own hand-written CSS — `.card`,
 * `.btn`, `.form-control`, `.stat-card` — redefined in a separate file per
 * page, which is how five pages ended up with four slightly different cards.
 * These constants are the replacement: one definition per element, shared by
 * every page, built on the brand/ink tokens in src/index.css.
 *
 * The public site has its own equivalent in components/ui/formStyles.js. That
 * one is deliberately separate: it styles rounded, marketing-weight forms,
 * where the dashboard wants denser, squarer controls.
 */

// --- Page scaffolding -------------------------------------------------------

export const pageTitleClass = "m-0 text-2xl font-bold text-ink-900 sm:text-3xl";

export const pageSubtitleClass = "mt-1 text-fluid-sm text-ink-500";

// --- Cards ------------------------------------------------------------------

export const cardClass =
  "overflow-hidden rounded-card border border-ink-200 bg-white shadow-card";

export const cardHeaderClass =
  "flex flex-col gap-3 border-b border-ink-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between";

export const cardTitleClass = "m-0 text-base font-semibold text-ink-900";

export const cardBodyClass = "p-5";

// --- Buttons ----------------------------------------------------------------

const buttonBase =
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-60";

export const btnPrimaryClass = `${buttonBase} border border-brand-600 bg-brand-600 text-white hover:bg-brand-700 disabled:hover:bg-brand-600`;

export const btnSecondaryClass = `${buttonBase} border border-ink-200 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600`;

export const btnGhostClass = `${buttonBase} border border-transparent bg-transparent text-ink-500 hover:bg-ink-100 hover:text-ink-900`;

export const btnDangerClass = `${buttonBase} border border-red-200 bg-white text-red-600 hover:border-red-500 hover:bg-red-50`;

// --- Forms ------------------------------------------------------------------

export const formLabelClass = "mb-1.5 block text-xs font-semibold text-ink-600";

export const formControlClass =
  "w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-fluid-sm text-ink-900 placeholder:text-ink-400 transition focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20 disabled:cursor-not-allowed disabled:bg-ink-50";

/** `formControlClass` for a field that failed validation — swap, never append. */
export const formControlErrorClass =
  "w-full rounded-lg border border-red-400 bg-white px-3 py-2.5 text-fluid-sm text-ink-900 placeholder:text-ink-400 transition focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20";

export const formErrorTextClass = "mt-1 block text-xs text-red-600";

export const requiredMarkClass = "text-red-600";

/** Two columns from `sm` up, one below. Pair with `formFullWidthClass`. */
export const formGridClass = "grid gap-4 sm:grid-cols-2";

export const formFullWidthClass = "sm:col-span-2";

// --- Segmented control ------------------------------------------------------

/**
 * A small group of mutually exclusive options (weekly/monthly, Person/Space).
 * Set `aria-pressed` on each button — the selected look is purely visual.
 */
export const segmentedGroupClass =
  "flex gap-1 rounded-lg bg-ink-100 p-1 text-center";

export const segmentedButtonClass = (isActive) =>
  `inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    isActive
      ? "bg-white text-brand-700 shadow-card"
      : "text-ink-500 hover:text-ink-900"
  }`;

// --- Tabs --------------------------------------------------------------------

/**
 * A page-level tab strip switching between whole sections (e.g. Site vs.
 * Widget), not a segmented toggle between a few values of one field — that
 * case is `segmentedGroupClass` above. Pair with `role="tablist"` /
 * `role="tab"` / `role="tabpanel"` on the markup; the active look is purely
 * visual, driven by `aria-selected`.
 */
export const tabListClass = "mb-6 flex gap-6 border-b border-ink-200";

export const tabButtonClass = (isActive) =>
  `-mb-px cursor-pointer border-b-2 py-2.5 text-fluid-sm font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
    isActive
      ? "border-brand-600 text-brand-700"
      : "border-transparent text-ink-500 hover:text-ink-900"
  }`;

// --- Alerts -----------------------------------------------------------------

const alertBase =
  "mb-5 flex items-start gap-2.5 rounded-lg border px-4 py-3 text-fluid-sm";

export const alertErrorClass = `${alertBase} border-red-200 bg-red-50 text-red-700`;

export const alertSuccessClass = `${alertBase} border-brand-200 bg-brand-50 text-brand-800`;

export const alertInfoClass = `${alertBase} border-ink-200 bg-ink-50 text-ink-600`;

// --- Grids ------------------------------------------------------------------

/**
 * Auto-fitting grids: they reflow on available width rather than on viewport
 * breakpoints, so they stay correct inside the narrower main column once the
 * sidebar appears at `lg`.
 */
export const infoGridClass =
  "grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(250px,100%),1fr))]";

export const statsGridClass =
  "grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(200px,100%),1fr))]";

export const listGridClass =
  "grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr))]";

// --- Modals -----------------------------------------------------------------

export const modalOverlayClass =
  "fixed inset-0 z-1100 flex items-center justify-center overflow-y-auto bg-black/50 p-4";

export const modalPanelClass =
  "flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-card bg-white shadow-card-hover";

export const modalHeaderClass =
  "flex shrink-0 items-center justify-between gap-3 border-b border-ink-200 px-5 py-4";

export const modalBodyClass = "flex-1 overflow-y-auto p-5";

export const modalFooterClass =
  "flex shrink-0 flex-col-reverse gap-2 border-t border-ink-200 px-5 py-4 sm:flex-row sm:justify-end";

export const modalCloseClass =
  "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

// --- Lists ------------------------------------------------------------------

export const listItemClass =
  "flex items-start justify-between gap-3 rounded-lg border border-ink-200 bg-white p-4 transition hover:border-brand-600 hover:shadow-card";

export const listItemTitleClass = "m-0 text-base font-semibold text-ink-900";

export const itemBadgeClass =
  "inline-flex items-center gap-1 rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-600";

const iconButtonBase =
  "flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

export const btnIconEditClass = `${iconButtonBase} border-ink-200 bg-white text-ink-500 hover:border-brand-600 hover:text-brand-600`;

export const btnIconDeleteClass = `${iconButtonBase} border-ink-200 bg-white text-ink-500 hover:border-red-500 hover:bg-red-50 hover:text-red-600`;

export const btnIconConfirmClass = `${iconButtonBase} border-ink-200 bg-white text-ink-500 hover:border-brand-600 hover:bg-brand-50 hover:text-brand-700`;

// --- Responsive data table --------------------------------------------------

/**
 * A table that becomes a stack of cards below `md`, rather than scrolling
 * sideways. Each `<td>` must carry `data-label="…"`: the cell label is drawn
 * from that attribute by `before:content-[attr(data-label)]` once the header
 * row is hidden, so a cell without one renders unlabelled on a phone.
 */
export const tableClass = "w-full border-collapse text-left";

export const tableHeadClass = "max-md:hidden";

export const tableHeaderCellClass =
  "border-b border-ink-200 px-3 py-3 text-xs font-semibold tracking-wide text-ink-500 uppercase";

export const tableBodyClass = "max-md:flex max-md:flex-col max-md:gap-3";

export const tableRowClass =
  "border-b border-ink-100 transition last:border-0 hover:bg-ink-50 max-md:flex max-md:flex-col max-md:gap-2 max-md:rounded-card max-md:border max-md:border-ink-200 max-md:bg-white max-md:p-4 max-md:shadow-card";

export const tableCellClass =
  "px-3 py-3 align-middle text-fluid-sm text-ink-700 max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:border-none max-md:px-0 max-md:py-1 max-md:before:min-w-25 max-md:before:shrink-0 max-md:before:text-xs max-md:before:font-semibold max-md:before:tracking-wide max-md:before:text-ink-500 max-md:before:uppercase max-md:before:content-[attr(data-label)]";

/** For the trailing actions cell: right-aligned and divided off on mobile. */
export const tableActionsCellClass = `${tableCellClass} max-md:mt-1 max-md:justify-end max-md:border-t max-md:border-ink-100 max-md:pt-3 max-md:before:hidden`;

// --- Misc -------------------------------------------------------------------

export const spinnerClass =
  "size-12 animate-spin rounded-full border-4 border-ink-200 border-t-brand-600 motion-reduce:animate-none";

export const badgeClass =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold";
